/**
 * @remarks RuntimeDocumentV1 校验、克隆与 sidecar Transformer
 * @module @yimoka/designable-playground/runtime
 */

import type { TreeNode } from '@yimoka/designable-core';

import { RUNTIME_DOCUMENT_SIDECAR_PROP } from '../shared/schema';
import type {
  IFormilySchemaDocument,
  IFormilySchemaInput,
} from '../transformers';
import type { ISchemaTransformer } from '../types';

import type {
  RuntimeAPIRequestConfigV1,
  RuntimeDictConfigV1,
  RuntimeDocumentMetadataV1,
  RuntimeDocumentV1,
  RuntimeEntityConfigV1,
  RuntimeJsonObject,
  RuntimeJsonValue,
  RuntimeStoreConfigV1,
} from './contracts';

import {
  RUNTIME_DOCUMENT_KIND,
  RUNTIME_DOCUMENT_VERSION,
  RUNTIME_SCHEMA_FIDELITY_PROP,
  RUNTIME_DOCUMENT_KEYS,
  RUNTIME_DOCUMENT_SIDECAR_KEYS,
  RUNTIME_SCOPE_RESERVED_KEYS,
  RuntimeDocumentValidationError,
  isObjectRecord,
  pushRuntimeIssue,
  cloneRuntimeJsonValue,
  validateAllowedKeys,
  validateRuntimeStoreConfig,
} from './document-core';
import type {
  IRuntimeSchemaFidelityV1,
  RuntimeDocumentSidecarV1,
  IRuntimeDocumentValidationIssue,
  IRuntimeDocumentValidationResult,
  IRuntimeEnvironmentScopeValidationResult,
} from './document-core';
import {
  validateRuntimeSchemaRoot,
  validateRuntimeEntityConfig,
  validateRuntimeMetadata,
  validateRuntimeDataBindings,
  createRuntimeEntitySchemaRoot,
  validateRuntimeFieldsConfigDataBindings,
  validateRuntimeI18nDataBindings,
  createRuntimeSchemaFidelity,
  restoreRuntimeSchemaFidelity,
} from './document-entity';
export {
  RuntimeDocumentValidationError,
} from './document-core';
export type {
  IRuntimeDocumentValidationIssue,
  IRuntimeDocumentValidationResult,
  IRuntimeEnvironmentScopeValidationResult,
} from './document-core';
export { isAllowedRuntimeDataKeyPath } from './document-expressions';
export { createRuntimeEntitySchemaRoot } from './document-entity';

/**
 * 判断输入是否意图使用 Runtime 文档合同。
 *
 * 携带 schema 且出现判别字段或 Runtime sidecar 时都按 Runtime 解析，
 * 防止缺失 kind/version 的畸形文档降级成旧格式并静默丢失 sidecar。
 * 旧纯 Schema 和只有 {form,schema} 的 package 仍由包装 Transformer 回退处理。
 */
export const isRuntimeDocumentCandidate = (value: unknown) => {
  try {
    if (
      !isObjectRecord(value)
      || !Object.prototype.hasOwnProperty.call(value, 'schema')
    ) {
      return false;
    }
    return ['kind', 'version', ...RUNTIME_DOCUMENT_SIDECAR_KEYS]
      .some(key => Object.prototype.hasOwnProperty.call(value, key));
  } catch {
    // 无法安全判别的代理对象必须进入严格 Runtime 校验，不能降级到宽松旧格式。
    return true;
  }
};

/** 安全确认 Runtime 根值是对象，代理陷阱等读取异常统一映射为非 JSON 错误。 */
const validateRuntimeDocumentRecord = (
  value: unknown,
  issues: IRuntimeDocumentValidationIssue[],
): value is Record<string, unknown> => {
  try {
    if (isObjectRecord(value)) return true;
  } catch {
    pushRuntimeIssue(
      issues,
      'runtime-non-json-value',
      'document',
      'Runtime 文档包含无法安全读取的非 JSON 对象',
    );
    return false;
  }
  pushRuntimeIssue(
    issues,
    'invalid-runtime-document',
    'document',
    'Runtime 文档必须是对象',
  );
  return false;
};

/** 不触发 getter 地读取 kind/version 数据属性，保证先判别版本再递归解析。 */
const readRuntimeDocumentDiscriminator = (
  value: Record<string, unknown>,
  issues: IRuntimeDocumentValidationIssue[],
) => {
  const discriminator: {
    kind?: unknown;
    version?: unknown;
  } = {};
  try {
    (['kind', 'version'] as const).forEach((key) => {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (
        descriptor
        && (!descriptor.enumerable || !('value' in descriptor))
      ) {
        pushRuntimeIssue(
          issues,
          'runtime-non-json-value',
          `document.${key}`,
          'Runtime 文档判别字段必须是可枚举的数据属性',
        );
        return;
      }
      discriminator[key] = descriptor?.value;
    });
  } catch {
    pushRuntimeIssue(
      issues,
      'runtime-non-json-value',
      'document',
      'Runtime 文档包含无法安全读取的判别字段',
    );
  }
  return discriminator;
};

/** 校验 RuntimeDocumentV1，并在成功时返回递归隔离的 JSON-safe 副本。 */
export const validateRuntimeDocument = (
  value: unknown,
): IRuntimeDocumentValidationResult => {
  const issues: IRuntimeDocumentValidationIssue[] = [];
  if (!validateRuntimeDocumentRecord(value, issues)) {
    return { issues, valid: false };
  }

  const { kind, version } = readRuntimeDocumentDiscriminator(value, issues);
  if (issues.length > 0) {
    return { issues, valid: false };
  }

  if (kind !== RUNTIME_DOCUMENT_KIND) {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-document',
      'document.kind',
      `Runtime 文档 kind 必须是 ${RUNTIME_DOCUMENT_KIND}`,
    );
    return { issues, valid: false };
  }
  if (version !== RUNTIME_DOCUMENT_VERSION) {
    const code = typeof version === 'number' && Number.isInteger(version)
      ? 'unsupported-runtime-document-version'
      : 'invalid-runtime-document';
    pushRuntimeIssue(
      issues,
      code,
      'document.version',
      `Runtime 文档仅支持版本 ${RUNTIME_DOCUMENT_VERSION}`,
    );
    return { issues, valid: false };
  }

  let cloned: RuntimeJsonValue | undefined;
  try {
    cloned = cloneRuntimeJsonValue(value, 'document', issues, new Set<object>());
  } catch {
    pushRuntimeIssue(
      issues,
      'runtime-non-json-value',
      'document',
      'Runtime 文档包含无法安全读取的非 JSON 对象',
    );
  }
  if (!isObjectRecord(cloned) || issues.length > 0) {
    return { issues, valid: false };
  }

  validateAllowedKeys(
    cloned,
    RUNTIME_DOCUMENT_KEYS,
    issues,
    'document',
    'invalid-runtime-document',
  );
  const effectiveSchemaRoot = createRuntimeEntitySchemaRoot(
    cloned.schema,
    cloned.entityConfig,
    cloned.store,
  );
  validateRuntimeSchemaRoot(
    cloned.schema,
    issues,
    'document.schema',
    effectiveSchemaRoot,
  );
  if (cloned.store !== undefined) {
    validateRuntimeStoreConfig(cloned.store, issues, 'document.store');
  }
  if (cloned.entityConfig !== undefined) {
    validateRuntimeEntityConfig(cloned.entityConfig, issues, 'document.entityConfig');
  }
  if (cloned.scope !== undefined) {
    if (!isObjectRecord(cloned.scope)) {
      pushRuntimeIssue(
        issues,
        'invalid-runtime-document',
        'document.scope',
        'scope 必须是 JSON 对象',
      );
    } else {
      Object.keys(cloned.scope).forEach((key) => {
        if (!RUNTIME_SCOPE_RESERVED_KEYS.has(key)) return;
        pushRuntimeIssue(
          issues,
          'invalid-runtime-document',
          `document.scope.${key}`,
          `scope.${key} 是运行时保留键，只能由受信宿主注入`,
        );
      });
    }
  }
  if (cloned.metadata !== undefined) {
    validateRuntimeMetadata(cloned.metadata, issues, 'document.metadata');
  }
  validateRuntimeDataBindings(
    cloned.schema,
    issues,
    'document.schema',
    effectiveSchemaRoot,
  );
  validateRuntimeFieldsConfigDataBindings(
    cloned.store,
    issues,
    'document.store',
    effectiveSchemaRoot,
  );
  validateRuntimeFieldsConfigDataBindings(
    cloned.entityConfig,
    issues,
    'document.entityConfig',
    effectiveSchemaRoot,
  );
  validateRuntimeI18nDataBindings(
    cloned.entityConfig,
    issues,
    'document.entityConfig',
  );

  return {
    document: issues.length ? undefined : cloned as unknown as RuntimeDocumentV1,
    issues,
    valid: issues.length === 0,
  };
};

/** 校验并克隆宿主 Runtime scope，禁止函数或实例越过纯数据表达式边界。 */
export const validateRuntimeEnvironmentScope = (
  value: unknown,
): IRuntimeEnvironmentScopeValidationResult => {
  if (value === undefined) {
    return { issues: [], scope: undefined, valid: true };
  }
  const issues: IRuntimeDocumentValidationIssue[] = [];
  let cloned: RuntimeJsonValue | undefined;
  try {
    cloned = cloneRuntimeJsonValue(value, 'environment.scope', issues, new Set<object>());
  } catch {
    pushRuntimeIssue(
      issues,
      'runtime-non-json-value',
      'environment.scope',
      'Runtime 宿主 scope 包含无法安全读取的非 JSON 对象',
    );
  }
  if (!isObjectRecord(cloned) && issues.length === 0) {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-document',
      'environment.scope',
      'Runtime 宿主 scope 必须是 JSON 对象',
    );
  }
  return {
    issues,
    scope: issues.length === 0 && isObjectRecord(cloned)
      ? cloned as RuntimeJsonObject
      : undefined,
    valid: issues.length === 0,
  };
};

/** 断言并返回安全副本，失败时抛出带稳定错误码的问题集合。 */
export const assertRuntimeDocument = (value: unknown): RuntimeDocumentV1 => {
  const result = validateRuntimeDocument(value);
  if (!result.valid || !result.document) {
    throw new RuntimeDocumentValidationError(result.issues);
  }
  return result.document;
};

/** 从已校验文档提取不含 schema 的原子 sidecar。 */
const createRuntimeDocumentSidecar = (
  document: RuntimeDocumentV1,
  fidelity: IRuntimeSchemaFidelityV1,
): RuntimeDocumentSidecarV1 => {
  const sidecar = Object.fromEntries(
    Object.entries(document).filter(([key]) => key !== 'schema'),
  ) as Omit<RuntimeDocumentV1, 'schema'>;
  return {
    ...sidecar,
    [RUNTIME_SCHEMA_FIDELITY_PROP]: fidelity,
  };
};

/** 清空 Runtime 字段树，同时保留根 Schema 元数据和全部 sidecar。 */
const createEmptyRuntimeDocument = (
  value: RuntimeDocumentV1,
): RuntimeDocumentV1 => {
  const document = assertRuntimeDocument(value);
  const schema = { ...document.schema };
  delete schema.required;
  return {
    ...document,
    schema: {
      ...schema,
      properties: {},
      type: 'object',
    },
  };
};

/**
 * 为现有 Formily Transformer 增加 RuntimeDocumentV1 sidecar。
 *
 * sidecar 只保存在设计树根的内部字段中，因此 schema 节点、导出 Formily
 * 文档和设计器物料都不会感知 Store/Entity 等运行时声明。
 */
export const createRuntimeDocumentTransformer = (
  baseTransformer: ISchemaTransformer<IFormilySchemaInput>,
): ISchemaTransformer<RuntimeDocumentV1> => {
  /** 把 Playground 通用危险键门禁映射为 Runtime 的稳定错误码和文档路径。 */
  const mapUnsafeSchemaObjectKeyError = (error: Error) => {
    const genericPath = error.message.slice('Schema 包含不安全字段路径：'.length);
    const path = genericPath.replace(/^Schema(?=\.|$)/, 'document');
    const pathSegments = path.split('.');
    const key = pathSegments[pathSegments.length - 1] ?? path;
    const issues: IRuntimeDocumentValidationIssue[] = [];
    pushRuntimeIssue(issues, 'runtime-unsafe-key', path, `Runtime 文档包含危险对象键 ${key}`);
    return new RuntimeDocumentValidationError(issues);
  };

  /** 在 Playground 通用 JSON 克隆前拒绝会被静默改写的 Runtime 输入。 */
  const prepareSchema = (schema: RuntimeDocumentV1) => assertRuntimeDocument(schema);

  /** 为 clearDesign 生成只清空 schema.properties 的完整 Runtime 快照。 */
  const createEmptySchema = (schema: RuntimeDocumentV1) => createEmptyRuntimeDocument(schema);

  /** 把 Runtime schema 交给基础 Transformer，并把其余字段写入根节点 marker。 */
  const toTreeNode = (value: RuntimeDocumentV1): TreeNode => {
    if (!isRuntimeDocumentCandidate(value)) {
      return baseTransformer.toTreeNode(value as unknown as IFormilySchemaInput);
    }

    const document = assertRuntimeDocument(value);
    const tree = baseTransformer.toTreeNode(document.schema);
    const normalizedSchema = baseTransformer.toSchema(tree) as IFormilySchemaDocument;
    tree.props = {
      ...tree.props,
      [RUNTIME_DOCUMENT_SIDECAR_PROP]: createRuntimeDocumentSidecar(
        document,
        createRuntimeSchemaFidelity(document.schema, normalizedSchema),
      ),
    };
    return tree;
  };

  /** 从根节点 marker 与基础 Formily schema 重组并复验完整 Runtime 文档。 */
  const toSchema = (tree: TreeNode): RuntimeDocumentV1 => {
    const sidecar = tree.props?.[RUNTIME_DOCUMENT_SIDECAR_PROP];
    if (sidecar === undefined) {
      // 兼容没有 Runtime marker 的旧纯 Schema/Package；旧入口仍能复用同一包装器。
      return baseTransformer.toSchema(tree) as unknown as RuntimeDocumentV1;
    }

    const sidecarRecord = isObjectRecord(sidecar) ? sidecar : {};
    const {
      [RUNTIME_SCHEMA_FIDELITY_PROP]: fidelity,
      ...documentSidecar
    } = sidecarRecord;
    const schema = restoreRuntimeSchemaFidelity(
      baseTransformer.toSchema(tree) as IFormilySchemaDocument,
      fidelity,
    );
    const document = {
      ...documentSidecar,
      schema,
    };
    return assertRuntimeDocument(document);
  };

  return {
    createEmptySchema,
    historySnapshotMode: 'atomic',
    mapUnsafeSchemaObjectKeyError,
    prepareSchema,
    toSchema,
    toTreeNode,
  };
};

export { RUNTIME_DOCUMENT_SIDECAR_PROP };

export type {
  RuntimeAPIRequestConfigV1,
  RuntimeDictConfigV1,
  RuntimeDocumentMetadataV1,
  RuntimeEntityConfigV1,
  RuntimeStoreConfigV1,
};
